import React, { useEffect, useState } from "react";
import Modal from "react-modal";
import "./project.css";
import { FaGithub, FaYoutube, FaFileAlt } from "react-icons/fa";

import project1Image from "../website pic/FMS.png";
import project2Image from "../website pic/eye.png";
import project3Image from "../website pic/pq.png";
import project4Image from "../website pic/project4.webp";

const projects = [
  {
    title: "Financial Management Website",
    role: "Full-Stack Developer",
    shortDescription:
      "A friendly financial toolbox with calculators for mortgages, cars, debt, vacations, and more—built to make money decisions feel less scary.",
    description:
      "A user-friendly platform designed to help anyone manage their finances with clarity. The site includes tools such as a mortgage calculator, car payment calculator, debt repayment advisor, vacation spending planner, luxury goods purchasing planner, and a spending tracker. Users can create accounts and log in to protect their information. The app follows a layered architecture: React.js on the frontend, Node.js on the backend, and MySQL for persistent storage.",
    tech: ["React.js", "Node.js", "MySQL", "Docker"],
    images: [project1Image],
    github: "https://github.com/Anmol1676/financial-management-website.git",
    video: "https://www.youtube.com/watch?v=9PSqUy1QQF8",
    doc: "",
    challenge: "Turn several intimidating financial calculations into one approachable, account-based experience.",
    contribution: "Built the React interface, Node.js service layer, authentication flow, MySQL persistence, and Docker development environment.",
    outcome: "Delivered one consistent workflow for six financial-planning tools instead of forcing users across disconnected calculators.",
  },
  {
    title: "Eye Focus-Assisted Cursor Teleportation (EFACT)",
    role: "HCI Research Project",
    shortDescription:
      "Eye-tracking + mouse input to teleport the cursor where you’re looking, so you never ‘lose’ your pointer again.",
    description:
      "EFACT addresses the common problem of losing track of the cursor after reading or scanning different areas of the screen. By combining eye tracking with mouse input, the system reduces both ballistic and corrective cursor movements. Users look at an area of interest and then move the mouse in that general direction; the cursor then teleports to their gaze point. Eye tracking is built with OpenCV for Python, and the app uses a Tkinter-based GUI. Libraries include cv2, dlib, pyautogui, sklearn, numpy, socket, tkinter, threading, struct, sqlite3, and matplotlib.",
    tech: ["Python", "OpenCV", "Tkinter", "SQLite"],
    images: [project2Image],
    github: "",
    video: "https://www.youtube.com/watch?v=5XKwmUyjXRc&t=17s",
    doc: "https://docs.google.com/document/d/1Khut4ej_5ZAX_2HwozmlXJfs82020QVrC9MLPmhwKkg/edit?usp=sharing",
    featured: true,
    challenge: "Help people relocate the pointer after visually scanning a dense screen without requiring a perfect gaze estimate.",
    contribution: "Designed the gaze-and-mouse interaction, built the OpenCV tracking pipeline and Tkinter interface, and documented the research prototype.",
    outcome: "Produced a working multimodal prototype and research paper demonstrating a distinctive approach to cursor reacquisition.",
  },
  {
    title: "Channel-Based Programming Q&A",
    role: "Full-Stack Developer",
    shortDescription:
      "Join channels, post questions, share code and screenshots—then discuss solutions in threaded comments.",
    description:
      "A channel-based Q&A platform for programmers. Users can log in, join channels, and create posts with code, text, and images. Other users can comment on posts and reply to those comments, keeping discussions organized. The frontend is built with React.js, the backend uses Node.js, and Docker is used to containerize the app for consistent deployment and scalability.",
    tech: ["React.js", "Node.js", "Docker"],
    images: [project3Image],
    github: "https://github.com/Anmol1676/programming-problem",
    video: "https://www.youtube.com/watch?v=OXFdxA2RpV4",
    doc: "",
    challenge: "Keep programming questions, code, screenshots, comments, and replies understandable inside topic-based communities.",
    contribution: "Implemented the React experience, Node.js APIs, channel membership, rich posts, and nested discussion model.",
    outcome: "Created an end-to-end collaboration prototype with authenticated, organized conversations around technical problems.",
  },
  {
    title: "Mission Red",
    role: "Game Developer",
    shortDescription:
      "A 2D survival adventure on Mars—fight aliens, explore shipwrecks, and craft weapons from what you find.",
    description:
      "Mission Red is a 2D adventure survival game that drops the player onto a hostile Mars. You traverse lava-scorched terrain, explore caves and abandoned shipwrecks, battle alien enemies, and collect resources to craft and upgrade weapons. Built in Unity, the game focuses on fast feedback, tight controls, and a visually striking red-planet aesthetic.",
    tech: ["Unity", "C#", "2D Game Design"],
    images: [project4Image],
    github: "https://github.com/Anmol1676/Mission-Red",
    video: "https://www.youtube.com/watch?v=COYZkuuvylY",
    doc: "",
    challenge: "Create a coherent survival loop across exploration, combat, collection, and crafting in a compact 2D game.",
    contribution: "Developed gameplay systems and level interactions in Unity and C#, including enemies, resources, and weapon progression.",
    outcome: "Delivered a playable Mars survival experience with a complete explore–fight–collect–upgrade loop.",
  },
];

function Project() {
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const displayedProjects = [...projects].sort(
    (a, b) => Number(Boolean(b.featured)) - Number(Boolean(a.featured))
  );

  useEffect(() => {
    Modal.setAppElement(document.getElementById("root") || document.body);
  }, []);

  const openModal = (project) => {
    setSelectedProject(project);
    setModalIsOpen(true);
  };

  const closeModal = () => {
    setModalIsOpen(false);
    setSelectedProject(null);
  };

  return (
    <section className="project-page">
      <div className="project-inner">
        <header className="project-header">
          <p className="project-kicker">Projects</p>
          <h1 className="project-title">Selected Builds</h1>
          <p className="project-subtitle">
            A mix of research prototypes, full-stack tools, and a Mars survival
            game—each one a little chapter in my dev story.
          </p>
        </header>

        <div className="project-grid">
          {displayedProjects.map((project, index) => (
            <article
              key={index}
              className={`project-card ${project.featured ? "featured" : ""}`}
            >
              <div className="project-card-image-wrapper">
                <img
                  src={project.images[0]}
                  alt={project.title}
                  className="project-card-image"
                  loading="lazy"
                  decoding="async"
                />
                <div className="project-card-overlay" />
                <div className="project-card-tag">{project.role}</div>
              </div>
              <div className="project-card-body">
                {project.featured && <span className="project-featured-label">Featured case study</span>}
                <h3 className="project-card-title">{project.title}</h3>
                <p className="project-card-desc">{project.shortDescription}</p>
                {project.tech && (
                  <div className="project-card-tech">
                    {project.tech.slice(0, 3).map((t) => (
                      <span key={t} className="project-tech-pill">
                        {t}
                      </span>
                    ))}
                    {project.tech.length > 3 && (
                      <span className="project-tech-pill more">+ more</span>
                    )}
                  </div>
                )}
                <div className="project-card-actions">
                  <button type="button" className="project-case-button" onClick={() => openModal(project)}>
                    View case study
                  </button>
                  {project.video && <a href={project.video} target="_blank" rel="noreferrer">Demo</a>}
                  {project.github && <a href={project.github} target="_blank" rel="noreferrer">Code</a>}
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>

      <Modal
        isOpen={modalIsOpen}
        onRequestClose={closeModal}
        shouldCloseOnOverlayClick={true}
        shouldCloseOnEsc={true}
        contentLabel="Project Details"
        className="project-modal"
        overlayClassName="project-modal-overlay"
      >
        {selectedProject && (
          <div className="project-modal-content">
            <button type="button" className="modal-close-btn" onClick={closeModal} aria-label="Close project details">
              ✕
            </button>

            <div className="modal-layout">
              <div className="modal-media">
                <div className="modal-media-frame">
                  {selectedProject.images.map((img, idx) => (
                    <img
                      key={idx}
                      src={img}
                      alt={`${selectedProject.title} screenshot ${idx + 1}`}
                      className="modal-image"
                      loading="lazy"
                      decoding="async"
                    />
                  ))}
                </div>
                {selectedProject.tech && (
                  <div className="modal-tech-row">
                    {selectedProject.tech.map((t) => (
                      <span key={t} className="modal-tech-pill">
                        {t}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <div className="modal-text">
                <p className="modal-role">{selectedProject.role}</p>
                <h2 className="modal-title">{selectedProject.title}</h2>

                <p className="modal-short">{selectedProject.shortDescription}</p>
                <p className="modal-description">
                  {selectedProject.description}
                </p>

                <div className="modal-case-grid">
                  <div><h3>Challenge</h3><p>{selectedProject.challenge}</p></div>
                  <div><h3>My contribution</h3><p>{selectedProject.contribution}</p></div>
                  <div><h3>Outcome</h3><p>{selectedProject.outcome}</p></div>
                </div>

                <div className="modal-links">
                  {selectedProject.video && (
                    <a
                      href={selectedProject.video}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="modal-link-btn youtube"
                    >
                      <FaYoutube />
                      <span>Demo Video</span>
                    </a>
                  )}
                  {selectedProject.github && (
                    <a
                      href={selectedProject.github}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="modal-link-btn github"
                    >
                      <FaGithub />
                      <span>Source Code</span>
                    </a>
                  )}
                  {selectedProject.doc && (
                    <a
                      href={selectedProject.doc}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="modal-link-btn doc"
                    >
                      <FaFileAlt />
                      <span>Project Paper</span>
                    </a>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </Modal>
    </section>
  );
}

export default Project;
